// ReviewsTestimonials.js
import React from 'react';
import { Star, Phone, Quote } from 'lucide-react';

const ReviewsTestimonials = () => {
  const testimonials = [];

  const stats = [];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-cream pt-16 pb-16 md:pt-24 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Reviews and Testimonials
          </p>
          <h1 className="font-display font-light text-ink leading-[1.1] text-4xl md:text-5xl mb-5">
            Stories From the Families We Care For
          </h1>
          <p className="text-muted text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Hear from the families we've had the privilege to serve. Their stories reflect our commitment to providing compassionate, quality care that makes a real difference in people's lives.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      {stats.length > 0 && (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="font-display font-light text-ink text-4xl md:text-5xl mb-2">
                    {stat.number}
                  </div>
                  <div className="text-muted text-sm leading-relaxed">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              In Their Words
            </p>
            <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl">
              What Families Are Saying
            </h2>
          </div>
          {testimonials.length === 0 && (
            <p className="text-muted text-base md:text-lg leading-relaxed text-center max-w-3xl mx-auto">
              We are honoured to support families in Queensland. As our community of clients grows, we look forward to sharing their experiences here. If we have cared for someone you love, we would warmly welcome your feedback.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-line relative">
                <Quote size={40} className="text-primary opacity-20 absolute top-4 left-4" />
                <div className="relative z-10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-line pt-4">
                    <div className="font-medium text-ink">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-muted">
                      {testimonial.location}
                    </div>
                    <div className="text-sm text-primary mt-1">
                      {testimonial.service}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Our Promise
          </p>
          <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-6">
            Your Trust Is Our Priority
          </h2>
          <p className="text-muted text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Earning your trust is at the heart of everything we do. Our purpose is simple: to provide care that genuinely makes a difference. We are committed to that trust through dependable service, compassionate carers, and steady dedication to the wellbeing of the people you love.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-5">
            Experience the Wei Care Difference
          </h2>
          <p className="text-muted text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Join the families who trust us with their loved ones' care. Contact us today to learn more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+61480804640"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
            >
              <Phone size={18} />
              Call +61 420 162 243
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 border border-line text-ink hover:border-primary hover:text-primary font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsTestimonials;
